import { Fragment, useCallback, useEffect, useRef, useState } from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import RefreshIcon from "@mui/icons-material/Refresh";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ReportProblemOutlinedIcon from "@mui/icons-material/ReportProblemOutlined";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import { toast } from "react-toastify";
import { kdlHealthService } from "../../utils/services/kdlHealthService";

const POLL_MS = 5000;
const font = { fontFamily: "poppins" };

// Plain-language presentation of each health level.
const LEVELS = {
  OK: { label: "Healthy", color: "success.main", bg: "rgba(46, 125, 50, 0.08)", Icon: CheckCircleOutlineIcon },
  WARN: { label: "Needs attention", color: "warning.main", bg: "rgba(237, 108, 2, 0.08)", Icon: ReportProblemOutlinedIcon },
  FAIL: { label: "Problem", color: "error.main", bg: "rgba(211, 47, 47, 0.08)", Icon: ErrorOutlineIcon },
  ERROR: { label: "Check failed", color: "error.main", bg: "rgba(211, 47, 47, 0.08)", Icon: ErrorOutlineIcon },
};
const SEVERITY = { FAIL: 0, ERROR: 0, WARN: 1, OK: 2 };

const DATASET_NAMES = {
  orders: "Orders",
  returns: "Returns",
  daily_inventory: "Daily inventory",
  inventories: "Inventory",
  purchase_orders: "Purchase orders",
  master_purchaseorders: "Master purchase orders",
  categories: "Categories",
  providers: "Providers",
  warehouses: "Warehouses",
  products: "Products",
};

const datasetName = (table = "") => {
  const key = table.replace(/^rl_/, "");
  if (DATASET_NAMES[key]) return DATASET_NAMES[key];
  const words = key.replace(/_/g, " ");
  return words.charAt(0).toUpperCase() + words.slice(1);
};

const fmtNum = (n) => (n === null || n === undefined ? "-" : Number(n).toLocaleString());
const fmtDate = (d) => (d ? new Date(d).toLocaleString() : "-");
const fmtAge = (h) => (h === null || h === undefined ? "-" : h < 24 ? `${h}h` : `${Math.round(h / 24)}d`);

const timeAgo = (d) => {
  if (!d) return null;
  const ms = Date.now() - new Date(d).getTime();
  if (Number.isNaN(ms)) return null;
  const min = Math.round(ms / 60000);
  if (min < 1) return "just now";
  if (min < 60) return `${min} min ago`;
  const h = Math.round(min / 60);
  if (h < 48) return `${h} hour${h === 1 ? "" : "s"} ago`;
  const days = Math.round(h / 24);
  return `${days} day${days === 1 ? "" : "s"} ago`;
};

const hoursText = (h) => (h < 48 ? `${Math.round(h)} hours` : `${Math.round(h / 24)} days`);

// Turn a technical issue from the health check into something an admin can act on.
const explainIssue = (raw) => {
  const msg = raw.replace(/^(WARN|FAIL):\s*/, "");
  let m;
  if ((m = msg.match(/^(\d+) duplicate _id rows/))) return `${fmtNum(m[1])} records appear more than once.`;
  if ((m = msg.match(/count differs from source: .*diff=([+-]?\d+)/))) {
    const diff = Number(m[1]);
    return diff < 0
      ? `${fmtNum(-diff)} records from the source system are missing here.`
      : `${fmtNum(diff)} more records here than in the source system.`;
  }
  if (msg.startsWith("catalog inconsistent")) return "Stored record counts don't add up — figures may be incomplete.";
  if (msg.startsWith("fragmented")) return "Stored in many small pieces — reports on this data may be slower.";
  if (msg.includes("delete files")) return "Many pending deletions — reports on this data may be slower.";
  if (msg.includes("still inlined")) return "Recent changes are not fully stored yet — reports may be slower.";
  if (msg.startsWith("compaction BLOCKED")) return "Automatic optimisation is stuck — a one-time full re-sync of this data set fixes it.";
  if ((m = msg.match(/^source has changes up to (\S+) that are not in the lake/)))
    return `The source has changes up to ${fmtDate(m[1])} that haven't arrived here — check that this data set is still syncing.`;
  if ((m = msg.match(/newest parquet file is ([\d.]+)h old/)))
    return `No new data has arrived in ${hoursText(Number(m[1]))} — check that this data set is still syncing.`;
  if (msg.includes("not readable") || msg.includes("not in ducklake_list_files") || msg.startsWith("newest parquet file has"))
    return "The most recent data could not be verified.";
  if (msg.startsWith("table not readable")) return "This data set could not be read.";
  return msg;
};

const freshnessOf = (t) => {
  const key = Object.keys(t).find((k) => k.startsWith("newest_") && k !== "newest_file_age_h" && k !== "newest_file_readable");
  const v = key && t[key];
  return v && v !== "None" ? v : null;
};

const StatusLabel = ({ status }) => {
  const lvl = LEVELS[status] || LEVELS.WARN;
  return (
    <Box sx={{ display: "inline-flex", alignItems: "center", gap: 0.75, color: lvl.color }}>
      <lvl.Icon sx={{ fontSize: 18 }} />
      <Typography sx={{ ...font, fontSize: "13px", fontWeight: 500 }}>{lvl.label}</Typography>
    </Box>
  );
};

const Banner = ({ health, running }) => {
  if (running) {
    return (
      <Box sx={{ display: "flex", alignItems: "center", gap: 2, p: 2, borderRadius: "10px", bgcolor: "action.hover" }}>
        <CircularProgress size={28} />
        <Box>
          <Typography sx={{ ...font, fontWeight: 600, fontSize: "16px" }}>Checking your data…</Typography>
          <Typography sx={{ ...font, fontSize: "13px", color: "var(--text-secondary)" }}>
            This usually takes a minute or two. You can keep this window open.
          </Typography>
        </Box>
      </Box>
    );
  }
  const tables = health.tables || [];
  const bad = tables.filter((t) => t.status === "FAIL").length;
  const warn = tables.filter((t) => t.status === "WARN").length;
  const status = health.error ? "ERROR" : health.status;
  const lvl = LEVELS[status] || LEVELS.WARN;
  const plural = (n) => `${n} data set${n === 1 ? "" : "s"}`;
  const copy = {
    OK: ["Everything looks good", `All ${plural(tables.length)} are complete and up to date.`],
    WARN: [
      `Mostly healthy — ${plural(warn)} need${warn === 1 ? "s" : ""} attention`,
      "Your data is usable. The items below are worth a look but are not urgent.",
    ],
    FAIL: [
      `Problem found in ${plural(bad)}`,
      "Some figures may be incomplete or out of date until this is fixed.",
    ],
    ERROR: ["The health check could not finish", "Try running it again. If it keeps failing, contact support."],
  }[status] || ["Status unknown", ""];
  const checked = health.checked_at || health.received_at;
  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 2, p: 2, borderRadius: "10px", bgcolor: lvl.bg }}>
      <lvl.Icon sx={{ fontSize: 40, color: lvl.color }} />
      <Box sx={{ flex: 1 }}>
        <Typography sx={{ ...font, fontWeight: 600, fontSize: "17px", color: "var(--text-primary)" }}>{copy[0]}</Typography>
        <Typography sx={{ ...font, fontSize: "13px", color: "var(--text-secondary)" }}>{copy[1]}</Typography>
      </Box>
      {checked && (
        <Tooltip title={fmtDate(checked)}>
          <Typography sx={{ ...font, fontSize: "12px", color: "var(--text-secondary)", whiteSpace: "nowrap" }}>
            Last checked {timeAgo(checked)}
          </Typography>
        </Tooltip>
      )}
    </Box>
  );
};

const StatCard = ({ label, value, hint }) => (
  <Box sx={{ flex: "1 1 160px", p: 1.75, border: "1px solid var(--border-color)", borderRadius: "10px" }}>
    <Typography sx={{ ...font, fontSize: "12px", color: "var(--text-secondary)" }}>{label}</Typography>
    <Typography sx={{ ...font, fontSize: "20px", fontWeight: 600, color: "var(--text-primary)", lineHeight: 1.4 }}>{value}</Typography>
    {hint && <Typography sx={{ ...font, fontSize: "11px", color: "var(--text-secondary)" }}>{hint}</Typography>}
  </Box>
);

const Summary = ({ health }) => {
  const tables = health.tables || [];
  const healthy = tables.filter((t) => t.status === "OK").length;
  const records = tables.reduce((sum, t) => sum + (Number(t.rows) || 0), 0);
  const latest = tables.map(freshnessOf).filter(Boolean).sort().pop();
  const syncs = health.syncs || [];
  const running = syncs.filter((s) => s.status === "syncing").length;
  const failed = syncs.filter((s) => s.status === "error" || s.last_error).length;
  return (
    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.5, mt: 2 }}>
      <StatCard label="Healthy data sets" value={`${healthy} of ${tables.length}`} />
      <StatCard label="Total records" value={fmtNum(records)} />
      <StatCard label="Latest data update" value={latest ? timeAgo(latest) : "-"} hint={latest ? fmtDate(latest) : null} />
      <StatCard
        label="Syncs"
        value={failed ? `${failed} failed` : running ? `${running} running` : "All idle"}
        hint={failed ? "See sync activity below" : running ? "Data is being refreshed" : "Nothing in progress"}
      />
    </Box>
  );
};

const headCell = { ...font, fontWeight: 600, fontSize: "12px", whiteSpace: "nowrap", color: "var(--text-secondary)" };
const bodyCell = { ...font, fontSize: "13px" };

const DataSets = ({ tables }) => {
  const sorted = [...tables].sort(
    (a, b) => (SEVERITY[a.status] ?? 1) - (SEVERITY[b.status] ?? 1) || datasetName(a.table).localeCompare(datasetName(b.table))
  );
  return (
    <TableContainer sx={{ border: "1px solid var(--border-color)", borderRadius: "10px" }}>
      <Table size="small">
        <TableHead>
          <TableRow>
            {["Data set", "Status", "Records", "Last updated", "Details"].map((h) => (
              <TableCell key={h} sx={headCell}>{h}</TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {sorted.map((t) => {
            const fresh = freshnessOf(t);
            const notes = (t.issues || []).map(explainIssue);
            return (
              <TableRow key={t.table}>
                <TableCell sx={{ ...bodyCell, fontWeight: 500 }}>{datasetName(t.table)}</TableCell>
                <TableCell sx={bodyCell}><StatusLabel status={t.status} /></TableCell>
                <TableCell sx={bodyCell}>{fmtNum(t.rows)}</TableCell>
                <TableCell sx={bodyCell}>
                  {fresh ? <Tooltip title={fmtDate(fresh)}><span>{timeAgo(fresh)}</span></Tooltip> : "-"}
                </TableCell>
                <TableCell sx={{ ...bodyCell, color: notes.length ? "var(--text-primary)" : "var(--text-secondary)" }}>
                  {notes.length
                    ? notes.map((n) => <div key={n}>{n}</div>)
                    : t.source_quiet
                      ? "Up to date — no new changes in the source system."
                      : "No issues"}
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

const SyncActivity = ({ health }) => {
  const watched = new Set((health.tables || []).map((t) => t.table));
  const syncs = (health.syncs || []).filter((s) => watched.has(s.table) || s.status !== "idle" || s.last_error);
  const stuck = new Set((health.locks || []).filter((l) => l.check).map((l) => String(l.source)));
  const items = syncs
    .filter((s) => s.status !== "idle" || s.last_error || stuck.has(String(s.id)))
    .map((s) => {
      const name = datasetName(s.table);
      if (stuck.has(String(s.id))) return { tone: "warning.main", text: `${name} has been syncing for over an hour — it may be stuck.` };
      if (s.status === "syncing") return { tone: "info.main", text: `${name} is syncing now.` };
      if (s.last_error) return { tone: "error.main", text: `${name}: last sync failed — ${s.last_error}` };
      return { tone: "var(--text-secondary)", text: `${name}: ${s.status}` };
    });
  return (
    <Box sx={{ border: "1px solid var(--border-color)", borderRadius: "10px", p: 1.75 }}>
      {items.length ? (
        items.map((i) => (
          <Typography key={i.text} sx={{ ...font, fontSize: "13px", color: i.tone, py: 0.25 }}>{i.text}</Typography>
        ))
      ) : (
        <Typography sx={{ ...font, fontSize: "13px", color: "var(--text-secondary)" }}>
          No syncs are running and none have failed recently.
        </Typography>
      )}
    </Box>
  );
};

const SYNC_RESULT = {
  completed: { label: "Completed", color: "success.main" },
  running: { label: "Running", color: "info.main" },
  failed: { label: "Failed", color: "error.main" },
  incomplete: { label: "Stopped early", color: "warning.main", hint: "The sync ended before reading all records — run it again." },
  interrupted: { label: "Interrupted", color: "warning.main", hint: "The sync never finished, usually because the data service restarted. Run it again." },
  skipped: { label: "Skipped", color: "var(--text-secondary)", hint: "Requested while this data set was already syncing, so it was ignored." },
};

const SYNC_TYPE = {
  full: { label: "Full refresh", hint: "Reloads the whole data set from the source." },
  adaptive: { label: "Update", hint: "Adds new and changed records, keeping existing ones." },
};

const fmtDuration = (s) => {
  if (s === null || s === undefined) return "-";
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ${s % 60}s`;
  return `${Math.floor(m / 60)}h ${m % 60}m`;
};

const RecentSyncs = ({ syncs, since }) => (
  <>
    {syncs.length ? (
      <TableContainer sx={{ border: "1px solid var(--border-color)", borderRadius: "10px", maxHeight: 360 }}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              {["When", "Data set", "Type", "Result", "Records", "Took", "Started by"].map((h) => (
                <TableCell key={h} sx={headCell}>{h}</TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {syncs.map((s) => {
              const result = SYNC_RESULT[s.status] || { label: s.status, color: "var(--text-secondary)" };
              const type = SYNC_TYPE[s.mode] || { label: s.mode };
              const muted = s.status === "skipped";
              return (
                <TableRow key={`${s.source_id}-${s.started_at}`} sx={muted ? { opacity: 0.6 } : undefined}>
                  <TableCell sx={bodyCell}>
                    <Tooltip title={fmtDate(s.started_at)}><span>{timeAgo(s.started_at)}</span></Tooltip>
                  </TableCell>
                  <TableCell sx={{ ...bodyCell, fontWeight: 500 }}>{datasetName(s.table)}</TableCell>
                  <TableCell sx={bodyCell}>
                    <Tooltip title={type.hint || ""}><span>{type.label}</span></Tooltip>
                  </TableCell>
                  <TableCell sx={{ ...bodyCell, color: result.color, fontWeight: 500 }}>
                    <Tooltip title={s.error ? `${result.hint || ""} (${s.error})` : result.hint || ""}><span>{result.label}</span></Tooltip>
                  </TableCell>
                  <TableCell sx={bodyCell}>{fmtNum(s.rows)}</TableCell>
                  <TableCell sx={bodyCell}>{fmtDuration(s.duration_s)}</TableCell>
                  <TableCell sx={{ ...bodyCell, color: s.user ? "var(--text-primary)" : "var(--text-secondary)" }}>
                    {s.user || (
                      <Tooltip title="The data service only records who started a full refresh of existing data.">
                        <span>Not recorded</span>
                      </Tooltip>
                    )}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    ) : (
      <Box sx={{ border: "1px solid var(--border-color)", borderRadius: "10px", p: 1.75 }}>
        <Typography sx={{ ...font, fontSize: "13px", color: "var(--text-secondary)" }}>No syncs in this period.</Typography>
      </Box>
    )}
    {since && (
      <Typography sx={{ ...font, fontSize: "11px", color: "var(--text-secondary)", mt: 0.75 }}>
        Showing syncs since {fmtDate(since)}. Sync history goes back at most 24 hours, and only to the data service's last restart.
      </Typography>
    )}
  </>
);

const Section = ({ title, children }) => (
  <Box sx={{ mt: 3 }}>
    <Typography sx={{ ...font, fontWeight: 600, fontSize: "14px", mb: 1, color: "var(--text-primary)" }}>{title}</Typography>
    {children}
  </Box>
);

const techHead = { ...font, fontWeight: 600, fontSize: "11px", whiteSpace: "nowrap" };
const techCell = { ...font, fontSize: "11px" };

const TechnicalDetails = ({ health }) => (
  <Accordion disableGutters elevation={0} sx={{ mt: 3, border: "1px solid var(--border-color)", borderRadius: "10px !important", "&:before": { display: "none" } }}>
    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
      <Typography sx={{ ...font, fontSize: "13px", color: "var(--text-secondary)" }}>Technical details (for support)</Typography>
    </AccordionSummary>
    <AccordionDetails>
      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mb: 1.5 }}>
        {health.lake && <Chip size="small" label={`Lake: ${health.lake}`} />}
        {health.trigger && <Chip size="small" label={`Trigger: ${health.trigger}${health.duration_s ? ` (${health.duration_s}s)` : ""}`} />}
        {health.service && (
          <Tooltip title={Object.entries(health.service.settings || {}).map(([k, v]) => `${k}=${v}`).join("  ")}>
            <Chip size="small" label={`KDL: ${health.service.status} · segfault restarts: ${health.service.segfault_restarts}${health.service.timeout_patch_mounted ? " · timeout patch" : ""}`} />
          </Tooltip>
        )}
        {health.snapshots && (
          <Chip size="small" label={`${fmtNum(health.snapshots.count)} snapshots, oldest ${fmtDate(health.snapshots.oldest_time)}`} />
        )}
      </Box>

      {health.error && (
        <Typography sx={{ mb: 1.5, fontFamily: "monospace", fontSize: "11px", color: "error.main", whiteSpace: "pre-wrap" }}>{health.error}</Typography>
      )}

      {health.tables?.length > 0 && (
        <TableContainer sx={{ border: "1px solid var(--border-color)", borderRadius: "8px" }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                {["Table", "Status", "Rows", "Dupes", "Files", "Rows/file", "Delete files", "Inlined", "Newest file"].map((h) => (
                  <TableCell key={h} sx={techHead}>{h}</TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {health.tables.map((t) => (
                <Fragment key={t.table}>
                  <TableRow>
                    <TableCell sx={techCell}>{t.table}</TableCell>
                    <TableCell sx={techCell}>{t.status}</TableCell>
                    <TableCell sx={techCell}>{fmtNum(t.rows)}</TableCell>
                    <TableCell sx={techCell}>{fmtNum(t.dup_ids)}</TableCell>
                    <TableCell sx={techCell}>{fmtNum(t.live_files)}</TableCell>
                    <TableCell sx={techCell}>{fmtNum(t.avg_rows_per_file)}</TableCell>
                    <TableCell sx={techCell}>{fmtNum(t.delete_files)}</TableCell>
                    <TableCell sx={techCell}>{fmtNum(t.inlined_rows)}</TableCell>
                    <TableCell sx={techCell}>{fmtAge(t.newest_file_age_h)}</TableCell>
                  </TableRow>
                  {t.issues?.length > 0 && (
                    <TableRow>
                      <TableCell colSpan={9} sx={{ ...techCell, color: "var(--text-secondary)", pt: 0, borderBottomStyle: "dashed" }}>
                        {t.issues.map((i) => <div key={i}>{i}</div>)}
                      </TableCell>
                    </TableRow>
                  )}
                </Fragment>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {health.maintenance?.length > 0 && (
        <Box sx={{ mt: 1.5 }}>
          <Typography sx={{ ...font, fontSize: "11px", fontWeight: 600 }}>Maintenance since KDL last started</Typography>
          {health.maintenance.map((m) => (
            <Typography key={m.event} sx={{ ...font, fontSize: "11px", color: m.event.includes("failed") ? "error.main" : "var(--text-secondary)" }}>
              {m.count} × {m.event}
            </Typography>
          ))}
        </Box>
      )}
    </AccordionDetails>
  </Accordion>
);

const KdlHealthModal = ({ open, onClose }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [requesting, setRequesting] = useState(false);
  const pollRef = useRef(null);

  const stopPolling = () => {
    if (pollRef.current) clearInterval(pollRef.current);
    pollRef.current = null;
  };

  const load = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const res = await kdlHealthService.fetch();
      setData(res);
      return res;
    } catch (e) {
      if (!silent) toast.error("Could not load data health");
      return null;
    } finally {
      if (!silent) setLoading(false);
    }
  }, []);

  const startPolling = useCallback(() => {
    stopPolling();
    pollRef.current = setInterval(async () => {
      const res = await load(true);
      if (res && !res.running) {
        stopPolling();
        if (res.request?.status === "completed") toast.success("Health check finished");
        if (res.request?.status === "failed") toast.error("Health check could not finish — please try again");
      }
    }, POLL_MS);
  }, [load]);

  useEffect(() => {
    if (!open) return stopPolling;
    load().then((res) => res?.running && startPolling());
    return stopPolling;
  }, [open, load, startPolling]);

  const runNow = async () => {
    setRequesting(true);
    try {
      const res = await kdlHealthService.run();
      setData((d) => ({ ...(d || {}), request: res.request, running: true }));
      toast.info(res.alreadyRequested ? "A health check is already running" : "Health check started — results in a minute or two");
      startPolling();
    } catch (e) {
      toast.error("Could not start a health check");
    } finally {
      setRequesting(false);
    }
  };

  const health = data?.health;
  const running = data?.running;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
        <Box sx={{ flex: 1 }}>
          <Typography sx={{ ...font, fontWeight: 600, fontSize: "17px" }}>Data Health</Typography>
          <Typography sx={{ ...font, fontSize: "12px", color: "var(--text-secondary)" }}>
            Checks that the data behind your reports and forecasts is complete and up to date.
          </Typography>
        </Box>
        <Button
          variant="contained"
          size="small"
          startIcon={running ? <CircularProgress size={14} color="inherit" /> : <RefreshIcon />}
          disabled={running || requesting}
          onClick={runNow}
          sx={{ ...font, textTransform: "none" }}
        >
          {running ? "Checking…" : "Run check now"}
        </Button>
        <IconButton onClick={onClose} size="small"><CloseIcon /></IconButton>
      </DialogTitle>

      <DialogContent dividers>
        {loading && !data ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}><CircularProgress /></Box>
        ) : !health ? (
          running ? (
            <Banner running />
          ) : (
            <Typography sx={{ ...font, color: "var(--text-secondary)", py: 4, textAlign: "center" }}>
              No health check has been run yet. Click "Run check now" to check your data.
            </Typography>
          )
        ) : (
          <>
            <Banner health={health} running={running} />
            {!health.error && <Summary health={health} />}

            {health.tables?.length > 0 && (
              <Section title="Data sets">
                <DataSets tables={health.tables} />
              </Section>
            )}

            {health.syncs && (
              <Section title="Sync activity">
                <SyncActivity health={health} />
              </Section>
            )}

            {Array.isArray(health.recent_syncs) && (
              <Section title="Recent syncs">
                <RecentSyncs syncs={health.recent_syncs} since={health.sync_log_since} />
              </Section>
            )}

            <TechnicalDetails health={health} />
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default KdlHealthModal;
